import React from "react";
import { launchPageSeconds, launchTemplates, type LaunchTemplate } from "@veyso/core";

/**
 * Which of the ten looks to make the film in.
 *
 * A list rather than a grid, because the art direction is the thing being
 * chosen between and ten names tell you nothing. The two colours and the
 * length carry most of the decision at a glance; the sentence settles it.
 *
 * `null` is a real answer and the default one — every look is meant to be a
 * fine outcome, so "whichever" is not a cop-out and should not be buried under
 * ten alternatives.
 */

/**
 * The two colours a look leads with.
 *
 * Hand-kept rather than parsed out of the art direction: four of the ten are
 * built around the customer's own accent and name no hex at all, so there is
 * nothing to read. The accent for those is the brand orange, which is what
 * they will actually be drawn in once the kit's colours reach the prompt.
 */
const SWATCH: Record<string, [string, string]> = {
  neon: ["#000000", "#C8FF3D"],
  soft: ["#FAF6F1", "#0F9E8E"],
  terminal: ["#150A0D", "#FF6B4A"],
  studio: ["#F5F6FA", "#5B45FF"],
  "warm-editorial": ["#F3E8D8", "#FF6900"],
  "cinematic-minimal": ["#FAFAF8", "#0A0A0C"],
  "playful-gradient": ["#FFFFFF", "#FF6900"],
  "dark-premium": ["#0B0B0D", "#FF6900"],
  "red-to-green": ["#DC2626", "#16A34A"],
  "kinetic-type": ["#EFECF5", "#FF6900"],
};

/** The first sentence or two — enough to choose by, short enough to scan. */
function gist(template: LaunchTemplate): string {
  const sentences = template.style.split(/(?<=\.)\s+/);
  let out = sentences[0] ?? template.style;
  if (out.length < 110 && sentences[1]) out += ` ${sentences[1]}`;
  return out;
}

export const StylePicker: React.FC<{
  value: string | null;
  onChange: (templateId: string | null) => void;
  disabled?: boolean;
  /**
   * The colour a film from a website will lead with, once read: shown on the
   * looks drawn in the brand, and "whichever" then picks only among those.
   */
  brandAccent?: string | null;
}> = ({ value, onChange, disabled, brandAccent }) => (
  <div className="flex flex-col gap-1.5">
    <button
      type="button"
      disabled={disabled}
      onClick={() => onChange(null)}
      aria-pressed={value === null}
      className={`flex items-center gap-3 rounded-[9px] border p-3 text-left transition-colors ${
        value === null ? "border-ember-500 bg-ember-500/5" : "border-[var(--edge)] hover:border-[var(--edge-strong)]"
      }`}
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[7px] bg-bay-800 text-[15px]">✦</span>
      <span className="min-w-0">
        <span className="block text-[13.5px] font-semibold text-ink-100">Whichever suits it</span>
        <span className="block text-[12px] leading-[1.45] text-ink-300">
          {brandAccent
            ? "One of the looks that use your colours is drawn at random."
            : "One is drawn at random. Every look is meant to be a fine outcome."}
        </span>
      </span>
    </button>

    {launchTemplates.map((template) => {
      const [ground, ownAccent] = SWATCH[template.id] ?? ["#EEF0F4", "#FF6900"];
      const accent = template.brandColours && brandAccent ? brandAccent : ownAccent;
      const chosen = value === template.id;
      return (
        <button
          key={template.id}
          type="button"
          disabled={disabled}
          onClick={() => onChange(template.id)}
          aria-pressed={chosen}
          className={`grid grid-cols-[38px_1fr] items-stretch gap-3 overflow-hidden rounded-[9px] border text-left transition-colors ${
            chosen ? "border-ember-500 bg-ember-500/5" : "border-[var(--edge)] hover:border-[var(--edge-strong)]"
          }`}
        >
          <span className="flex flex-col" aria-hidden="true">
            <span className="flex-1" style={{ background: ground }} />
            <span className="flex-1" style={{ background: accent }} />
          </span>
          <span className="min-w-0 py-2.5 pr-3">
            <span className="flex flex-wrap items-baseline gap-2">
              <span className="text-[13.5px] font-semibold text-ink-100">{template.name}</span>
              <span className="tnum text-[10.5px] text-ink-400">
                {template.seconds ?? launchPageSeconds}s{template.seconds ? " · teaser" : ""}
              </span>
              {template.brandColours ? (
                <span className="text-[10.5px] font-medium text-ink-300">Uses your colours</span>
              ) : (
                <span className="text-[10.5px] text-ink-400">Its own colours</span>
              )}
            </span>
            <span className="mt-0.5 block text-[12px] leading-[1.45] text-ink-300">{gist(template)}</span>
          </span>
        </button>
      );
    })}
  </div>
);
